import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { deleteAppDataRow, queryUserAppData } from "../../services/appData";
import { sortRows, summarizeAppData } from "../../utils/appData";
import JsonTree from "./JsonTree";
import { SortHeader } from "./Sortable";
import DeleteRowDialog from "./DeleteRowDialog";
import BulkDeleteDialog from "./BulkDeleteDialog";
import { rowKey, useRowSelection } from "./selection";
import Pager from "./Pager";
import {
  Actions,
  Button,
  DataRow,
  ErrorBanner,
  Field,
  HeadRow,
  Input,
  Label,
  Mono,
  Muted,
  Row,
  Small,
  Table,
  Toolbar,
  Truncate,
  Wrapper,
} from "./AppDataStyles";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const identity = (user) => {
  if (!user) return "—";
  const name = [user.firstname, user.lastname].filter(Boolean).join(" ");
  return name || user.email || user.public_id;
};

/**
 * Query app data across every user in the client (the endpoint excludes global rows).
 */
export const QueryAppDataPanel = ({ clientId = null }) => {
  const [filters, setFilters] = useState({ q: "", name: "", key: "", dataType: "", email: "", userId: "" });
  const [applied, setApplied] = useState({ q: "", name: "", key: "", dataType: "", email: "", userId: "" });
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await queryUserAppData({
      clientId,
      q: applied.q || null,
      name: applied.name || null,
      key: applied.key || null,
      email: applied.email || null,
      userId: applied.userId || null,
      page,
      pageSize,
      sort: sortKey,
      direction: sortDir,
    });

    setLoading(false);

    if (result?.success === false) {
      setRows([]);
      setError(result.message || "Could not query app data.");
      return;
    }

    setRows(sortRows(result.records ?? [], sortKey, sortDir));
    setPagination(result.pagination ?? null);
  }, [clientId, applied, page, pageSize, sortKey, sortDir]);

  useEffect(() => {
    load();
  }, [load]);

  // The endpoint has no `data_type` parameter, so it filters the returned page.
  const term = applied.dataType.trim().toLowerCase();
  const matchedRows = term
    ? rows.filter((row) => String(row.data_type ?? "").toLowerCase().includes(term))
    : rows;

  const visibleRows = matchedRows;
  const selection = useRowSelection(visibleRows);

  const bulkDelete = async (force) => {
    const targets = selection.rows;

    if (targets.length === 0) return;

    setBulkBusy(true);
    setBulkError(null);

    let failed = 0;
    let lastError = null;

    for (const row of targets) {
      // eslint-disable-next-line no-await-in-loop
      const result = await deleteAppDataRow(row.public_id, { force, clientId });

      if (result?.success === false) {
        failed += 1;
        lastError = result.message;
      }
    }

    setBulkBusy(false);
    await load();

    if (failed > 0) {
      setBulkError(`${failed} of ${targets.length} failed${lastError ? `: ${lastError}` : ""}.`);
      return;
    }

    setBulkOpen(false);
    selection.clear();
  };

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "updated_at" ? "desc" : "asc");
    }

    setPage(1);
  };

  const remove = async (row, force = false) => {
    if (!row) return;

    setDeleting(true);
    setDeleteError(null);

    const result = await deleteAppDataRow(row.public_id, { force, clientId });

    setDeleting(false);

    if (result?.success === false) {
      setDeleteError(result.message || "Could not delete the row.");
      return;
    }

    setPendingDelete(null);
    setSelectedRow(null);
    await load();
  };

  const apply = () => {
    setPage(1);
    setApplied({ ...filters });
    setSelectedRow(null);
  };

  const reset = () => {
    const empty = { q: "", name: "", key: "", dataType: "", email: "", userId: "" };
    setFilters(empty);
    setApplied(empty);
    setPage(1);
    setSelectedRow(null);
  };

  const field = (key, label, placeholder) => (
    <Field style={{ minWidth: "150px", flex: "1 1 150px" }}>
      <Label>{label}</Label>
      <Input
        value={filters[key]}
        placeholder={placeholder}
        onChange={(event) => setFilters((current) => ({ ...current, [key]: event.target.value }))}
        onKeyDown={(event) => {
          if (event.key === "Enter") apply();
        }}
      />
    </Field>
  );

  return (
    <Wrapper>
      <Toolbar>
        {field("q", "Search", "text in data")}
        {field("name", "Name", "onboarding_answers")}
        {field("key", "Key", "v1")}
        {field("dataType", "Data type", "json")}
        {field("email", "Email", "user@example.com")}
        {field("userId", "User id", "user public id")}
      </Toolbar>

      <Toolbar>
        <Button type="button" onClick={apply} disabled={loading}>
          <Icon icon="lucide:search" /> Query
        </Button>
        <Small type="button" onClick={reset}>
          <Icon icon="lucide:x" /> Reset
        </Small>

        {selection.count > 0 ? (
          <>
            <Small
              $danger
              type="button"
              onClick={() => {
                setBulkOpen(true);
                setBulkError(null);
              }}
            >
              <Icon icon="lucide:trash-can-outline" /> Delete selected ({selection.count})
            </Small>
            <Small type="button" onClick={selection.clear}>
              Clear
            </Small>
          </>
        ) : null}

        <span style={{ flex: 1 }} />

        <select
          value={pageSize}
          onChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(1);
          }}
          style={{
            padding: "4px 8px",
            fontSize: "12px",
            color: "var(--main-font-color)",
            backgroundColor: "var(--main-bg)",
            border: "1px solid var(--main-border-color)",
            borderRadius: "6px",
          }}
        >
          {[25, 50, 100, 200].map((size) => (
            <option key={size} value={size}>{size} / page</option>
          ))}
        </select>
      </Toolbar>

      <Pager pagination={pagination} onPage={(next) => setPage(next)} loading={loading} />

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}

      <Table>
        <HeadRow style={{ gridTemplateColumns: "30px 1.3fr 1fr 0.7fr 1.4fr 1fr auto" }}>
          <input
            type="checkbox"
            checked={selection.allSelected}
            onChange={selection.toggleAll}
            title="Select all rows on this page"
            style={{ cursor: "pointer" }}
          />
          <SortHeader label="User" column="user_email" activeKey={sortKey} dir={sortDir} onToggle={toggleSort} />
          <SortHeader label="Name" column="name" activeKey={sortKey} dir={sortDir} onToggle={toggleSort} />
          <SortHeader label="Key" column="key" activeKey={sortKey} dir={sortDir} onToggle={toggleSort} />
          <span>Data</span>
          <SortHeader label="Updated" column="updated_at" activeKey={sortKey} dir={sortDir} onToggle={toggleSort} />
          <span />
        </HeadRow>

        {loading ? (
          <Row style={{ gridTemplateColumns: "1fr", cursor: "default" }}>
            <Muted>Querying…</Muted>
          </Row>
        ) : visibleRows.length === 0 ? (
          <Row style={{ gridTemplateColumns: "1fr", cursor: "default" }}>
            <Muted>No rows match.</Muted>
          </Row>
        ) : (
          visibleRows.map((row) => (
            <React.Fragment key={row.public_id ?? `${row.name}/${row.key}/${row.user?.public_id}`}>
            <DataRow
              $active={selectedRow?.public_id === row.public_id}
              style={{ gridTemplateColumns: "30px 1.3fr 1fr 0.7fr 1.4fr 1fr auto" }}
              onClick={() =>
                setSelectedRow((current) =>
                  current?.public_id === row.public_id ? null : row
                )
              }
            >
              <span onClick={(event) => event.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selection.isSelected(rowKey(row))}
                  onChange={() => selection.toggle(rowKey(row))}
                  style={{ cursor: "pointer" }}
                />
              </span>
              <span>
                <Truncate>{identity(row.user)}</Truncate>
                <span style={{ display: "block", fontSize: "11px", color: "#a0a6b1" }}>{row.user?.email ?? ""}</span>
              </span>
              <Truncate>{row.name}</Truncate>
              <Mono>{row.key}</Mono>
              <Truncate style={{ color: "#a0a6b1" }}>{summarizeAppData(row.data)}</Truncate>
              <span>{formatDate(row.updated_at)}</span>

              <Actions>
                <Small
                  $danger
                  type="button"
                  title="Delete this row"
                  onClick={(event) => {
                    event.stopPropagation();
                    setPendingDelete(row);
                    setDeleteError(null);
                  }}
                >
                  <Icon icon="lucide:trash-can-outline" />
                </Small>
              </Actions>
            </DataRow>

            {selectedRow?.public_id === row.public_id ? (
              <div
                style={{
                  padding: "10px 12px 14px",
                  borderBottom: "1px solid var(--content-item-border-color)",
                  backgroundColor: "var(--main-bg)",
                }}
              >
                <JsonTree value={row.data} />
              </div>
            ) : null}
            </React.Fragment>
          ))
        )}
      </Table>

      <BulkDeleteDialog
        show={bulkOpen}
        count={selection.count}
        hasLocked={selection.rows.some((row) => row.locked)}
        busy={bulkBusy}
        error={bulkError}
        onClose={() => setBulkOpen(false)}
        onConfirm={bulkDelete}
      />

      <DeleteRowDialog
        show={Boolean(pendingDelete)}
        row={pendingDelete}
        scope="user"
        busy={deleting}
        error={deleteError}
        onClose={() => setPendingDelete(null)}
        onConfirm={(force) => remove(pendingDelete, force)}
      />
    </Wrapper>
  );
};

export default QueryAppDataPanel;
